import { FC, useEffect, useState } from 'react'
import { Icon } from '@liveconnect/icons'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { FormProvider, FormTextInput, useTheme } from '@liveconnect/components'

import { useBlockRouteChangeWithDialog } from '../../../../utils/routing/useBlockRouteChange'
import useTenantDesign from '../../../../core/tenantDesign/useTenantDesign'
import useDesignConstants from '../../hooks'
import useNotifications from '../../../../utils/notifications/useNotifications'
import useUi from '../../../../core/ui/useUi'

import { Color, ConfigurationProps } from '../../types'
import { DropdownConfigurationFormProps } from './types'

import './styles.scss'

const DropdownConfiguration: FC<ConfigurationProps> = ({
  colors,
  triggerCancel,
  formCancelled,
  triggerSubmit,
  formSubmitted,
  formDirty,
}) => {
  const { t } = useTranslation()
  const { changeThemeVars } = useTheme()
  const { defaultTheme } = useDesignConstants()
  const { editDesign } = useTenantDesign()
  const notify = useNotifications()
  const { showConfirmation } = useUi()

  const [showResetModal, setShowResetModal] = useState<boolean>(false)

  const methods = useForm<DropdownConfigurationFormProps>({
    mode: 'onChange',
    defaultValues: {
      'select-chevron': colors['select-chevron'],
    },
  })

  const {
    control,
    reset,
    getValues,
    formState: { isDirty },
  } = methods

  const handleChangeColor = (color: Record<string, string>) => {
    changeThemeVars(color)
  }

  const handleResetColor = () => {
    reset()
    handleChangeColor({ ...getValues() })
    formCancelled()
    notify.success(t('tenantDesign.configuration.notification'))
  }

  const submitDesign = async () => {
    try {
      const data: Color[] = await Object.entries({ ...getValues() }).map(
        (color) => ({ key: color[0], value: color[1] })
      )
      await editDesign(data, 'dropdown')
      reset({ ...getValues() })
      formSubmitted()
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    if (triggerCancel()) handleResetColor()
  }, [triggerCancel])

  useEffect(() => {
    if (triggerSubmit()) submitDesign()
  }, [triggerSubmit])

  useEffect(() => {
    formDirty(isDirty)
  }, [isDirty])

  useEffect(() => {
    showResetModal &&
      showConfirmation({
        title: t('tenantDesign.configuration.reset'),
        subtitle: t('tenantDesign.configuration.modal.body'),
        confirmText: t('tenantDesign.configuration.modal.reset'),
        cancelText: t('common.cancel'),
        iconName: 'report_problem',
        onConfirm: () => {
          handleResetColor()
          setShowResetModal(false)
        },
        onCancel: () => {
          setShowResetModal(false)
        },
      })
  }, [showResetModal])

  useEffect(() => {
    return () => changeThemeVars(defaultTheme)
  }, [])

  useBlockRouteChangeWithDialog(isDirty)

  return (
    <div className="dropdown-design-configuration">
      <FormProvider methods={methods}>
        <div className="dropdown-design-configuration__title">
          {t('tenantDesign.configuration.title')}
        </div>
        <hr className="dropdown-design-configuration__separator" />
        <div className="dropdown-design-configuration__body">
          <div className="dropdown-design-configuration__body__picker d-flex align-items-center">
            <div className="dropdown-design-configuration__body__picker__label">
              {t('tenantDesign.dropdown.icon')}
            </div>
            <FormTextInput
              control={control}
              name="select-chevron"
              type="color"
              onChange={(name, value) =>
                handleChangeColor({ [name]: value.toString() })
              }
            />
          </div>
        </div>
        <div
          className="dropdown-design-configuration__footer d-flex align-items-center"
          onClick={() => isDirty && setShowResetModal(true)}
        >
          <div className="dropdown-design-configuration__footer__label">
            {t('tenantDesign.configuration.reset')}
          </div>
          <Icon
            className="dropdown-design-configuration__footer__icon"
            name="upgrade"
          />
        </div>
      </FormProvider>
    </div>
  )
}

export default DropdownConfiguration
